/**
 * Das Prueffeld einer Kreditkartenabrechnung, rechts neben dem Original.
 * Steht in Auszug.tsx an der Stelle des Prueffelds der Kontoauszuege, das
 * dort unveraendert bleibt.
 *
 * Anders als beim Kontoauszug: Salden heissen Schuld oder Guthaben statt
 * Minus, Buchungen stehen als Belastung und Gutschrift aus Sicht des
 * Karteninhabers, dazu Kaufdatum, Fremdwaehrung und der Einzug vom
 * Girokonto (nur zur Info, keine Buchung). Korrigiert wird ueber die
 * Kartenrouten, nie ueber die Routen der Kontoauszuege.
 */
import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiFehler, type Auszugansicht, type Buchung, type Kartenabrechnung, type Kartenansicht } from '../lib/api'
import { datum, euro, iban, zeitpunkt, zeitraum } from '../lib/format'
import { pfad } from '../lib/art'
import { endeKurz, KLASSE_TEXT, LEER, NUR_CAMT, saldoWort } from '../lib/karte'
import { Fenster } from './Fenster'
import { MandantWaehlen } from './MandantWaehlen'
import { ArtFestlegen, AuszugLoeschen, Freigeben } from './AuszugFormulare'
import { KartenKopfBearbeiten } from './KartenKopfBearbeiten'
import { KartenBuchungBearbeiten } from './KartenBuchungBearbeiten'

const buchungSpalten = { gridTemplateColumns: '64px minmax(0, 1fr) 96px 96px' }

/** Befunde, bei denen "Karte neu lesen" der naheliegende Ausweg ist. */
/** Kopf einer Karte, die der Kartenleser nicht lesen konnte. Mit dem ersten Speichern entsteht er von Hand. */
function leererKopf(auszugId: string): Kartenabrechnung {
  return {
    auszug_id: auszugId, kreditkarte_id: null, kennung: null, anbieter_gedruckt: null, endziffern: null, kartenart: null,
    anfangssaldo_gedruckt: null, schlusssaldo_gedruckt: null, anfangssaldo_art: null, schlusssaldo_art: null, saldo_beleg: null,
    legende: null, abrechnungsdatum: null, faelliger_betrag_cent: null, mindestbetrag_cent: null, ausgleich_konto: null,
    ausgleich_datum: null, ausgleich_betrag_cent: null, hinweis: null, gelesen_am: '',
  }
}

const NEU_LESEN_BEFUNDE = new Set(['karte_nicht_gelesen', 'karte_spiegel', 'karte_trennlauf'])

function summenStimmen(soll: string | null, haben: string | null, saldo: { summeSoll: string; summeHaben: string }): boolean {
  return (soll === null || soll === saldo.summeSoll) && (haben === null || haben === saldo.summeHaben)
}

export function KartenAnalysefeld({ daten, zurueck, springen }: {
  daten: Auszugansicht
  zurueck: string
  springen: (seite: number) => void
}) {
  const abfragen = useQueryClient()
  const navigiere = useNavigate()
  const { auszug, buchungen, befunde, saldo, dokument, mandant } = daten
  const [bearbeiten, setzeBearbeiten] = useState<Buchung | 'neu' | null>(null)
  const [kopfOffen, setzeKopfOffen] = useState(false)
  const [freigabeOffen, setzeFreigabeOffen] = useState(false)
  const [loeschenOffen, setzeLoeschenOffen] = useState(false)
  const [zuordnenOffen, setzeZuordnenOffen] = useState(false)
  const [artOffen, setzeArtOffen] = useState(false)
  const [neuLesenOffen, setzeNeuLesenOffen] = useState(false)
  const [laeuft, setzeLaeuft] = useState(false)
  const [fehler, setzeFehler] = useState<string | null>(null)

  const karte = useQuery({
    queryKey: ['kartenabrechnung', auszug.id],
    queryFn: () => api.holen<Kartenansicht>(`/api/karten/abrechnung/${auszug.id}`),
  })
  const k = karte.data
  const abr = k?.abrechnung ?? null
  const gelesen = abr !== null
  const exportiert = auszug.stand === 'exportiert'

  const markiert = new Map<number, string>()
  for (const b of befunde) for (const p of b.positionen ?? []) if (!markiert.has(p)) markiert.set(p, b.titel)
  const blockend = befunde.filter((b) => b.blockt)
  const hinweise = befunde.filter((b) => !b.blockt && b.art !== 'scan')
  const freigebbar = !exportiert && blockend.length > 0 && blockend.every((b) => b.freigebbar) && auszug.stand !== 'freigegeben'
  const anbieter = abr?.anbieter_gedruckt ?? auszug.bank
  const kartenText = abr ? [anbieter, endeKurz(abr.endziffern)].filter(Boolean).join(' ') || LEER : auszug.art_beleg ?? LEER

  async function tun(arbeit: () => Promise<unknown>, rueckfall: string): Promise<boolean> {
    setzeFehler(null)
    setzeLaeuft(true)
    try {
      await arbeit()
      return true
    } catch (problem) {
      setzeFehler(problem instanceof ApiFehler ? problem.message : rueckfall)
      return false
    } finally {
      setzeLaeuft(false)
    }
  }

  async function vorschlagUebernehmen() {
    const v = k?.vorschlag
    if (!v) return
    const ok = await tun(
      () => api.senden(`/api/karten/abrechnung/${auszug.id}/zuordnen`, { mandantId: v.mandantId, kreditkarteId: v.kreditkarteId, karteMerken: false }),
      'Die Zuordnung hat nicht geklappt.',
    )
    if (ok) await abfragen.invalidateQueries()
  }

  async function neuLesen() {
    const ok = await tun(() => api.senden(`/api/karten/abrechnung/${auszug.id}/neu-lesen`), 'Die Karte konnte nicht neu gelesen werden.')
    setzeNeuLesenOffen(false)
    if (ok) {
      await abfragen.invalidateQueries()
      // Der Auszug ist geloescht und entsteht beim Lesen neu, mit neuer Kennung.
      navigiere(pfad('kreditkarte', '/eingang'))
    }
  }

  return (
    <div className="analysefeld">
      <div className="analysefeld-kopf">
        <div className="analysefeld-titel">
          <h2>Gelesen</h2>
          <span className="leiser">{buchungen.length} {buchungen.length === 1 ? 'Buchung' : 'Buchungen'}</span>
        </div>
        {!exportiert && (
          <div className="analysefeld-werkzeuge">
            <button type="button" className="knopf klein" onClick={() => setzeKopfOffen(true)}>{gelesen ? 'Kartenkopf ändern' : 'Kartenkopf erfassen'}</button>
            {gelesen && <button type="button" className="knopf klein" onClick={() => setzeBearbeiten('neu')}>Buchung hinzufügen</button>}
            <button type="button" className="knopf klein" onClick={() => setzeNeuLesenOffen(true)}>Karte neu lesen</button>
          </div>
        )}
      </div>

      <div className="analysefeld-koerper">
        {fehler && <div className="fehler abstand">{fehler}</div>}

        {/* ── Befunde ──────────────────────────────────────── */}
        {(blockend.length > 0 || auszug.stand === 'freigegeben') && (
          <div className="befunde">
            {blockend.map((b) => (
              <div key={b.art} className={`befund ${b.freigebbar ? 'warnung' : 'schlecht'}`}>
                <strong>{b.titel}</strong>
                <span>{b.text}</span>
                {b.positionen && b.positionen.length > 0 && <span className="leiser">Zeile {b.positionen.join(', ')}</span>}
                {b.bezug && !b.bezug.startsWith('spur-') && <Link to={`/auszuege/${b.bezug}`} className="verweis">Zur anderen Abrechnung</Link>}
                {b.art === 'art_unsicher' && !exportiert && (
                  <button type="button" className="knopf klein befund-knopf" onClick={() => setzeArtOffen(true)}>Art festlegen …</button>
                )}
                {NEU_LESEN_BEFUNDE.has(b.art) && !exportiert && (
                  <button type="button" className="knopf klein befund-knopf" onClick={() => setzeNeuLesenOffen(true)}>Karte neu lesen …</button>
                )}
              </div>
            ))}
            {auszug.stand === 'freigegeben' && (
              <div className="befund freigegeben">
                <strong>Trotz Befund freigegeben</strong>
                <span>{auszug.freigabe_grund}</span>
                {auszug.freigegeben_am && <span className="leiser">{zeitpunkt(auszug.freigegeben_am)}</span>}
              </div>
            )}
            {freigebbar && (
              <div className="befund-aktion">
                <button type="button" className="knopf klein" onClick={() => setzeFreigabeOffen(true)}>Trotzdem freigeben …</button>
              </div>
            )}
          </div>
        )}
        {blockend.length === 0 && auszug.stand !== 'freigegeben' && !exportiert && (
          <div className="befunde">
            <div className="befund gut">
              <strong>Geht auf</strong>
              <span>Alter Saldo und Buchungen ergeben den neuen Saldo auf den Cent{dokument.hat_textebene ? ', und jeder Betrag steht so im Original' : ''}. {mandant ? (NUR_CAMT ? 'Bereit für den Export.' : 'Bereit für den Buchungsstapel.') : 'Fehlt nur noch der Mandant.'}</span>
            </div>
          </div>
        )}
        {hinweise.length > 0 && (
          <div className="befunde hinweise">
            {hinweise.map((b) => (
              <div key={b.art} className="befund hinweis">
                <strong>{b.titel}</strong>
                <span>{b.text}</span>
                {b.positionen && b.positionen.length > 0 && <span className="leiser">Zeile {b.positionen.join(', ')}</span>}
                {b.bezug && !b.bezug.startsWith('spur-') && <Link to={`/auszuege/${b.bezug}`} className="verweis">Zur anderen Abrechnung</Link>}
              </div>
            ))}
          </div>
        )}

        {/* ── Vorschlag über die Endziffern: nie automatisch ─────── */}
        {k?.vorschlag && !mandant && !exportiert && (
          <div className="befunde">
            <div className="befund hinweis">
              <strong>Vorschlag</strong>
              <span>{k.vorschlag.text}</span>
              <button type="button" className="knopf klein befund-knopf" disabled={laeuft} onClick={() => void vorschlagUebernehmen()}>Übernehmen</button>
            </div>
          </div>
        )}

        {/* ── Mandant und Kartenkopf ───────────────────────── */}
        <dl className="kopfdaten">
          <div className="kopfdaten-mandant">
            <dt>Mandant</dt>
            <dd>
              {mandant ? (
                <Link to={pfad('kreditkarte', `/mandanten/${mandant.id}`)} className="verweis">{mandant.name}</Link>
              ) : (
                <span className="text-warnung">Nicht zugeordnet</span>
              )}
              {!exportiert && (
                <button type="button" className="knopf still klein" onClick={() => setzeZuordnenOffen(true)}>
                  {mandant ? 'Ändern' : 'Zuordnen'}
                </button>
              )}
            </dd>
          </div>
          <div><dt>Karte</dt><dd className="zahl" title={k?.kennungLesbar || undefined}>{kartenText}</dd></div>
          <div><dt>Karteninhaber</dt><dd>{auszug.inhaber ?? LEER}</dd></div>
          <div><dt>Zeitraum</dt><dd className="zahl">{zeitraum(auszug.zeitraum_von, auszug.zeitraum_bis)}</dd></div>
          {abr?.abrechnungsdatum && <div><dt>Abrechnungsdatum</dt><dd className="zahl">{datum(abr.abrechnungsdatum)}</dd></div>}
          {abr?.faelliger_betrag_cent && <div><dt>Fälliger Betrag</dt><dd className="zahl">{euro(abr.faelliger_betrag_cent)} €</dd></div>}
          {abr?.mindestbetrag_cent && <div><dt>Mindestbetrag</dt><dd className="zahl">{euro(abr.mindestbetrag_cent)} €</dd></div>}
          {abr && (abr.ausgleich_datum || abr.ausgleich_betrag_cent || abr.ausgleich_konto) && (
            <div>
              <dt>Einzug</dt>
              <dd>
                <span className="zahl">
                  {[abr.ausgleich_datum ? datum(abr.ausgleich_datum) : null, abr.ausgleich_betrag_cent ? `${euro(abr.ausgleich_betrag_cent)} €` : null].filter(Boolean).join(', ')}
                </span>
                {abr.ausgleich_konto && <div className="leiser zahl">{iban(abr.ausgleich_konto)}</div>}
                <div className="leiser">nur zur Info, keine Buchung</div>
              </dd>
            </div>
          )}
          <div>
            <dt>Art</dt>
            <dd>
              Kreditkarte
              {!exportiert && <button type="button" className="knopf still klein" onClick={() => setzeArtOffen(true)}>Ändern</button>}
            </dd>
          </div>
        </dl>

        {/* ── Buchungen ─────────────────────────────────────── */}
        {gelesen && (
          <div className="buchungen">
            <div className="buchung-kopf" style={buchungSpalten}>
              <div>Tag</div>
              <div>Händler und Text</div>
              <div className="rechts">Belastung</div>
              <div className="rechts">Gutschrift</div>
            </div>
            {buchungen.length === 0 && <div className="leiser buchung-leer">Keine Buchungen auf dieser Abrechnung.</div>}
            {buchungen.map((b) => {
              const z = k?.zusatz[b.id] ?? null
              const kaufdatum = z?.belegdatum && z.belegdatum !== b.buchungsdatum ? z.belegdatum : null
              const zusatzkarte = z?.endziffern && z.endziffern !== abr?.endziffern ? z.endziffern : null
              const fremd = z?.originalbetrag_cent && z.originalwaehrung ? `${euro(z.originalbetrag_cent)} ${z.originalwaehrung}` : null
              const betrag = (
                <div className="zahl rechts betrag" title={z?.kurs ? `Kurs ${z.kurs}` : undefined}>
                  <div>{euro(b.betrag_cent)}</div>
                  {fremd && <div className="nebensache">{fremd}</div>}
                </div>
              )
              return (
                <button
                  key={b.id}
                  type="button"
                  className={`buchung${markiert.has(b.position) ? ' markiert' : ''}`}
                  style={buchungSpalten}
                  onClick={() => {
                    if (b.seite) springen(b.seite)
                    if (!exportiert) setzeBearbeiten(b)
                  }}
                >
                  <div className="zahl leiser">{datum(b.buchungsdatum).slice(0, 6)}</div>
                  <div className="buchung-text">
                    <div className="buchung-partner">
                      {b.buchungstext ?? 'Ohne Text'}
                      {z?.klasse && <span className="marke">{KLASSE_TEXT[z.klasse]}</span>}
                      {zusatzkarte && <span className="marke">Karte {endeKurz(zusatzkarte)}</span>}
                      {b.von_hand && <span className="marke">von Hand</span>}
                      {b.nachgelesen && <span className="marke">nachgelesen</span>}
                    </div>
                    <div className="nebensache">{[kaufdatum ? `Kaufdatum ${datum(kaufdatum)}` : null, b.verwendungszweck].filter(Boolean).join(' · ') || ' '}</div>
                  </div>
                  {b.richtung === 'DBIT' ? betrag : <div />}
                  {b.richtung === 'CRDT' ? betrag : <div />}
                </button>
              )
            })}
          </div>
        )}

        {/* ── Die Saldo-Rechnung aus Sicht des Karteninhabers ──── */}
        {gelesen && saldo && (
          <div className="saldorechnung">
            <div className="leiser">Schuld alt, plus Belastungen, minus Gutschriften, ergibt Schuld neu.</div>
            <div><span>Alter Saldo laut Abrechnung</span><span className="zahl">{saldoWort(auszug.anfangssaldo_cent)}</span></div>
            <div><span>plus Belastungen</span><span className="zahl">{euro(saldo.summeSoll)} €</span></div>
            <div><span>minus Gutschriften</span><span className="zahl">{euro(saldo.summeHaben)} €</span></div>
            <div className="summe"><span>ergibt</span><span className="zahl">{saldoWort(saldo.errechnet)}</span></div>
            <div><span>Neuer Saldo laut Abrechnung</span><span className="zahl">{saldoWort(auszug.schlusssaldo_cent)}</span></div>
            {(auszug.summe_soll_gedruckt_cent !== null || auszug.summe_haben_gedruckt_cent !== null) && (
              <div className="gegenprobe">
                <span>Summen laut Abrechnung</span>
                <span className="zahl">
                  {auszug.summe_soll_gedruckt_cent !== null ? `Belastungen ${euro(auszug.summe_soll_gedruckt_cent)}` : ''}
                  {auszug.summe_soll_gedruckt_cent !== null && auszug.summe_haben_gedruckt_cent !== null ? ', ' : ''}
                  {auszug.summe_haben_gedruckt_cent !== null ? `Gutschriften ${euro(auszug.summe_haben_gedruckt_cent)}` : ''}
                </span>
                <span className={`stand ${summenStimmen(auszug.summe_soll_gedruckt_cent, auszug.summe_haben_gedruckt_cent, saldo) ? 'bereit' : 'doppelt'}`}>
                  {summenStimmen(auszug.summe_soll_gedruckt_cent, auszug.summe_haben_gedruckt_cent, saldo) ? 'stimmen mit den Buchungen' : 'weichen ab'}
                </span>
              </div>
            )}
            <div className={`ergebnis ${saldo.gehtAuf === true ? 'gut' : saldo.gehtAuf === false ? 'schlecht' : 'offen'}`}>
              <span className={`stand ${saldo.gehtAuf === true ? 'bereit' : saldo.gehtAuf === false ? 'doppelt' : 'pruefen'}`}>
                {saldo.gehtAuf === true ? 'geht auf' : saldo.gehtAuf === false ? `Differenz ${euro(saldo.differenz)}` : 'nicht prüfbar'}
              </span>
            </div>
          </div>
        )}

        {abr?.hinweis && (
          <div className="hinweisliste">
            <div className="hinweiszeile"><strong>Hinweis des Kartenlesers.</strong> {abr.hinweis}</div>
          </div>
        )}

        {!exportiert && (
          <div className="analysefeld-schluss">
            <button type="button" className="knopf still klein loeschknopf" onClick={() => setzeLoeschenOffen(true)}>Abrechnung löschen</button>
          </div>
        )}
      </div>

      {kopfOffen && k && <KartenKopfBearbeiten auszug={auszug} abrechnung={abr ?? leererKopf(auszug.id)} offen aufSchliessen={() => setzeKopfOffen(false)} />}
      {bearbeiten && (
        <KartenBuchungBearbeiten
          key={bearbeiten === 'neu' ? 'neu' : bearbeiten.id}
          auszugId={auszug.id}
          buchung={bearbeiten === 'neu' ? null : bearbeiten}
          zusatz={bearbeiten === 'neu' ? null : k?.zusatz[bearbeiten.id] ?? null}
          offen
          aufSchliessen={() => setzeBearbeiten(null)}
        />
      )}
      {/* Kein errechneter Saldo: der Hinweis im Freigabefenster spricht von camt, der Buchungsstapel traegt keinen Saldo. */}
      <Freigeben
        auszugId={auszug.id}
        befunde={befunde}
        errechnet={null}
        gedruckt={auszug.schlusssaldo_cent}
        offen={freigabeOffen}
        aufSchliessen={() => setzeFreigabeOffen(false)}
      />
      <AuszugLoeschen auszugId={auszug.id} zurueck={zurueck} offen={loeschenOffen} aufSchliessen={() => setzeLoeschenOffen(false)} />
      {zuordnenOffen && (
        <MandantWaehlen offen aufSchliessen={() => setzeZuordnenOffen(false)} auszugId={auszug.id} iban={null} inhaber={auszug.inhaber} karte={{ auszugId: auszug.id }} />
      )}
      {artOffen && <ArtFestlegen auszug={auszug} offen aufSchliessen={() => setzeArtOffen(false)} />}

      <Fenster titel="Karte neu lesen" offen={neuLesenOffen} aufSchliessen={() => { if (!laeuft) setzeNeuLesenOffen(false) }}>
        <div className="formular">
          <p className="leise">Die Abrechnung wird gelöscht und neu gelesen. Von Hand geänderte Buchungen gehen verloren.</p>
          <p className="leiser">Die übrigen Auszüge derselben Datei bleiben, wie sie sind. Die neu gelesene Abrechnung erscheint danach im Eingang.</p>
          <div className="formular-fuss">
            <button type="button" className="knopf still" onClick={() => setzeNeuLesenOffen(false)} disabled={laeuft}>Abbrechen</button>
            <button type="button" className="knopf gefuellt" onClick={() => void neuLesen()} disabled={laeuft}>{laeuft ? 'Einen Moment …' : 'Neu lesen'}</button>
          </div>
        </div>
      </Fenster>
    </div>
  )
}
